import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import EditionEditorActions, { sendBlockReason } from './EditionEditorActions';
import EditionFields from './EditionFields';
import EditionItemsPanel from './EditionItemsPanel';
import ItemPickerDialog from './ItemPickerDialog';
import SendPreviewDialog from './SendPreviewDialog';
import StatusChip from '../StatusChip';
import useEditionEditor from '../../hooks/useEditionEditor';
import { EDITION_STATUS, editionTitle, formatDateTime } from '../../changelogAdminConfig';

/**
 * Editor de um envio (rascunho ou agendado): campos do e-mail à esquerda, itens à direita.
 * As ações (prévia, agendar, enviar, cancelar) gravam antes e são executadas pela aba.
 *
 * @param {{ editionId: number|null, busy: boolean, isSendingPreview: boolean, onClose: Function,
 *   onSaved: Function (manual: boolean), actions: { preview, sendPreview, schedule, unschedule, sendNow, cancel } }} props
 */
function EditionEditorDialog({ editionId, busy, isSendingPreview, onClose, onSaved, actions }) {
  const fullScreen = useMediaQuery('(max-width: 900px)');
  const editor = useEditionEditor(editionId);
  const { edition, form, items } = editor;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [previewToOpen, setPreviewToOpen] = useState(false);

  const handleClose = () => {
    if (editor.isSaving) return;
    if (editor.isDirty) setConfirmDiscard(true);
    else onClose();
  };

  // Grava se houver mudança e depois executa a ação com o envio atualizado
  const run = (action) => () => editor.saveIfDirty((saved) => {
    if (editor.isDirty) onSaved(false);
    action(saved);
  });

  const blockReason = sendBlockReason({ subject: form.subject, itemCount: items.length, blockedCount: editor.blockedCount });
  const status = edition ? EDITION_STATUS[edition.status] : null;

  return (
    <Dialog open={editionId !== null && editionId !== undefined} onClose={handleClose} fullWidth maxWidth="lg" fullScreen={fullScreen}>
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>
            {edition ? editionTitle(edition) : 'Envio'}
          </Typography>
          {status && <StatusChip info={status} />}
          {edition?.status === 'scheduled' && (
            <Typography variant="caption" color="text.secondary">para {formatDateTime(edition.scheduled_at)}</Typography>
          )}
          {editor.isDirty && <Typography variant="caption" color="warning.main">Alterações não salvas</Typography>}
        </Box>
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12 }} aria-label="Fechar">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#F8F9FC' }}>
        {editor.error && <Alert severity="error" onClose={editor.clearError} sx={{ mb: 2 }}>{editor.error}</Alert>}
        {editor.isLoading || !edition ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            {editor.isLoading && <CircularProgress size={32} />}
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(300px, 380px) 1fr' } }}>
            <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <EditionFields form={form} setField={editor.setField} number={edition.number} />
            </Box>
            <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
              <EditionItemsPanel
                items={items}
                blockedCount={editor.blockedCount}
                onAdd={() => setPickerOpen(true)}
                onMove={editor.moveItem}
                onToggleHighlight={editor.toggleHighlight}
                onRemove={editor.removeItem}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      {edition && (
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <EditionEditorActions
            edition={edition}
            blockReason={blockReason}
            busy={busy}
            isSaving={editor.isSaving}
            isSendingPreview={isSendingPreview}
            onCancelEdition={() => actions.cancel(edition)}
            onPreview={run(actions.preview)}
            onSendPreview={() => setPreviewToOpen(true)}
            onSchedule={run(actions.schedule)}
            onUnschedule={run(actions.unschedule)}
            onSendNow={run(actions.sendNow)}
            onSave={() => editor.save(() => onSaved(true))}
          />
        </DialogActions>
      )}

      {edition && (
        <ItemPickerDialog
          open={pickerOpen}
          editionId={edition.id}
          existingIds={new Set(items.map((item) => item.id))}
          onClose={() => setPickerOpen(false)}
          onAdd={editor.addItems}
        />
      )}

      <SendPreviewDialog
        edition={edition}
        open={previewToOpen}
        onClose={() => setPreviewToOpen(false)}
        onSubmit={(to) => {
          setPreviewToOpen(false);
          run((saved) => actions.sendPreview(saved, to))();
        }}
      />

      <ConfirmDialog
        isOpen={confirmDiscard}
        title="Sair sem salvar?"
        message="As mudanças deste envio ainda não foram salvas."
        confirmLabel="Sair sem salvar"
        variant="warning"
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </Dialog>
  );
}

export default EditionEditorDialog;
